import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { DataTable, type DataTableColumn } from '@/components/data-display/data-table'
import { DataTableColumnSelector } from '@/components/data-display/data-table-column-selector'
import { Pagination } from '@/components/data-display/pagination'
import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { SearchInput } from '@/components/forms/search-input'
import { FilterBar } from '@/components/layout/filter-bar'
import { PageHeader } from '@/components/layout/page-header'
import { ActionMenu } from '@/components/overlays/action-menu'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'
import { useAdminListControls } from '@/features/admin/shared/use-admin-list-controls'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import { ApiError } from '@/lib/api/http-client'
import actionDeleteIconUrl from '@/assets/action-menu/delete.svg'
import actionEditIconUrl from '@/assets/action-menu/edit.svg'

import {
  appTagFormSchema,
  createAdminAppTag,
  deleteAdminAppTag,
  listAdminAppTagApps,
  listAdminAppTags,
  type AdminAppTag,
  type AppTagFormValues,
  type TagBoundApp,
  updateAdminAppTag,
} from './api'

const pageSizeOptions = [10, 20, 50]
const defaultAdminAppTagColumnIds = [
  'name',
  'app-count',
  'created-by',
  'updated-by',
  'updated-at',
  'actions',
]

type DialogMode = 'create' | 'rename'
type AdminAppTagSortField = 'created_at' | 'name' | 'updated_at'
type AdminAppTagSortOrder = 'asc' | 'desc'

interface TagFormDialogProps {
  defaultName?: string
  isSubmitting: boolean
  mode: DialogMode
  onClose: () => void
  onSubmit: (values: AppTagFormValues) => Promise<void>
  open: boolean
  rootError?: string
}

interface DeleteTagDialogProps {
  apps: TagBoundApp[]
  isLoadingApps: boolean
  isSubmitting: boolean
  onClose: () => void
  onConfirm: () => void
  open: boolean
  rootError?: string
  tag?: AdminAppTag
}

function PlusIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />
    </svg>
  )
}

function AppCountCell({ count }: { count: number }) {
  return <span className="tabular-nums">{count}</span>
}

function formatDateTime(value: string | null) {
  if (!value) {
    return '-'
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return value.length > 0 ? value : '-'
  }

  return new Intl.DateTimeFormat('zh-CN', {
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
    .format(date)
    .replaceAll('/', '-')
}

function operatorDisplayName(name?: string | null, id?: string | null) {
  const normalizedName = name?.trim()
  if (normalizedName) {
    return normalizedName
  }

  const normalizedId = id?.trim()
  if (!normalizedId) {
    return '-'
  }

  return normalizedId.length > 12 ? `${normalizedId.slice(0, 8)}...` : normalizedId
}

function OperatorCell({ id, name }: { id?: string | null; name?: string | null }) {
  const text = operatorDisplayName(name, id)
  const title = name?.trim() ? undefined : (id ?? undefined)

  return (
    <span className="block max-w-[180px] truncate" title={title}>
      {text}
    </span>
  )
}

function TagFormDialog({
  defaultName = '',
  isSubmitting,
  mode,
  onClose,
  onSubmit,
  open,
  rootError,
}: TagFormDialogProps) {
  const { t } = useTranslation('adminAppTags')
  const {
    clearErrors,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm<AppTagFormValues>({
    defaultValues: {
      name: defaultName,
    },
  })

  useEffect(() => {
    if (open) {
      reset({ name: defaultName })
      clearErrors()
    }
  }, [clearErrors, defaultName, open, reset])

  const submit = handleSubmit(async (values) => {
    const parsed = appTagFormSchema.safeParse(values)

    if (!parsed.success) {
      setError('name', { message: t('validation.nameRequired') })
      return
    }

    await onSubmit(parsed.data)
  })

  const title = mode === 'create' ? t('createDialog.title') : t('renameDialog.title')
  const confirm = mode === 'create' ? t('createDialog.confirm') : t('renameDialog.confirm')

  return (
    <Modal
      closeLabel={t('dialog.close')}
      footer={
        <>
          <Button disabled={isSubmitting} onClick={onClose} size="sm" variant="secondary">
            {t('dialog.cancel')}
          </Button>
          <Button isLoading={isSubmitting} size="sm" type="submit" form="admin-app-tag-form">
            {confirm}
          </Button>
        </>
      }
      onClose={onClose}
      open={open}
      title={title}
    >
      <form className="grid gap-4" id="admin-app-tag-form" onSubmit={submit}>
        <FormField
          error={errors.name?.message}
          htmlFor="admin-app-tag-name"
          label={t('form.nameLabel')}
          required
        >
          <Input
            autoComplete="off"
            autoFocus
            id="admin-app-tag-name"
            invalid={Boolean(errors.name)}
            placeholder={t('form.namePlaceholder')}
            {...register('name', {
              onChange: () => {
                clearErrors('name')
                clearErrors('root')
              },
            })}
          />
        </FormField>
        {rootError ? (
          <p
            className="rounded-md bg-red-50 px-3 py-2 text-sm leading-5 text-[var(--color-danger)]"
            role="alert"
          >
            {rootError}
          </p>
        ) : null}
      </form>
    </Modal>
  )
}

function DeleteTagDialog({
  apps,
  isLoadingApps,
  isSubmitting,
  onClose,
  onConfirm,
  open,
  rootError,
  tag,
}: DeleteTagDialogProps) {
  const { t } = useTranslation('adminAppTags')

  return (
    <Modal
      closeLabel={t('dialog.close')}
      footer={
        <>
          <Button disabled={isSubmitting} onClick={onClose} size="sm" variant="secondary">
            {t('dialog.cancel')}
          </Button>
          <Button isLoading={isSubmitting} onClick={onConfirm} size="sm" variant="danger">
            {t('deleteDialog.confirm')}
          </Button>
        </>
      }
      onClose={onClose}
      open={open}
      subtitle={
        tag ? t('deleteDialog.subtitleWithName', { name: tag.name }) : t('deleteDialog.subtitle')
      }
      title={t('deleteDialog.title')}
    >
      <div className="grid gap-3">
        <div className="max-h-72 overflow-y-auto rounded-md border border-[var(--color-border)] bg-[#fbfbfb] p-2">
          {isLoadingApps ? (
            <div className="grid gap-2 p-2">
              {Array.from({ length: 3 }, (_, index) => (
                <div
                  aria-hidden
                  className="h-9 animate-pulse rounded-md bg-[var(--color-skeleton)]"
                  key={index}
                />
              ))}
            </div>
          ) : apps.length > 0 ? (
            <ul className="grid gap-2">
              {apps.map((app) => (
                <li
                  className="rounded-md border border-[rgba(26,28,25,0.08)] bg-white px-3 py-2 text-sm text-[var(--color-text)]"
                  key={app.id}
                >
                  {app.name}
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-3 py-4 text-sm text-[var(--color-text-muted)]">
              {t('deleteDialog.noApps')}
            </p>
          )}
        </div>
        {rootError ? (
          <p
            className="rounded-md bg-red-50 px-3 py-2 text-sm leading-5 text-[var(--color-danger)]"
            role="alert"
          >
            {rootError}
          </p>
        ) : null}
      </div>
    </Modal>
  )
}

function resolveMutationErrorMessage(error: unknown, t: ReturnType<typeof useTranslation>['t']) {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return t('errors.permissionDenied')
    }

    if (error.message === 'Tag name is required') {
      return t('validation.nameRequired')
    }

    if (error.message === 'Tag already exists') {
      return t('errors.duplicateName')
    }

    if (error.message === 'Tag does not exist') {
      return t('errors.notFound')
    }
  }

  return t('errors.operationFailed')
}

export function AdminAppTagsPage() {
  const { t } = useTranslation('adminAppTags')
  const queryClient = useQueryClient()
  const { showFeedback } = useFeedback()
  const {
    page,
    pageSize,
    search,
    searchInput,
    setPage,
    setPageSize,
    setSearchInput,
    setVisibleColumnIds,
    visibleColumnIds,
    visibleColumnIdSet,
  } = useAdminListControls({ defaultColumnIds: defaultAdminAppTagColumnIds })
  const [sortField, setSortField] = useState<AdminAppTagSortField>('name')
  const [sortOrder, setSortOrder] = useState<AdminAppTagSortOrder>('asc')
  const [formMode, setFormMode] = useState<DialogMode>('create')
  const [formTarget, setFormTarget] = useState<AdminAppTag | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<AdminAppTag | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [boundApps, setBoundApps] = useState<TagBoundApp[]>([])
  const [formError, setFormError] = useState<string | undefined>()
  const [deleteError, setDeleteError] = useState<string | undefined>()

  const tagsQuery = useQuery({
    placeholderData: (previous) => previous,
    queryFn: () =>
      listAdminAppTags({
        order: sortOrder,
        orderBy: sortField,
        page,
        pageSize,
        search,
      }),
    queryKey: ['admin-app-tags', { page, pageSize, search, sortField, sortOrder }],
  })

  const invalidateTagLists = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin-app-tags'] })
  }

  const createMutation = useMutation({
    mutationFn: createAdminAppTag,
    onSuccess: async () => {
      setFormOpen(false)
      setFormError(undefined)
      setPage(1)
      showFeedback({
        message: t('feedback.createSuccess'),
        scope: 'global',
        tone: 'success',
      })
      await invalidateTagLists()
    },
    onError: (error) => {
      setFormError(resolveMutationErrorMessage(error, t))
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ tagId, values }: { tagId: string; values: AppTagFormValues }) =>
      updateAdminAppTag(tagId, values),
    onSuccess: async () => {
      setFormOpen(false)
      setFormTarget(null)
      setFormError(undefined)
      showFeedback({
        message: t('feedback.updateSuccess'),
        scope: 'global',
        tone: 'success',
      })
      await invalidateTagLists()
    },
    onError: (error) => {
      setFormError(resolveMutationErrorMessage(error, t))
    },
  })

  const loadBoundAppsMutation = useMutation({
    mutationFn: listAdminAppTagApps,
    onError: (error) => {
      setDeleteError(resolveMutationErrorMessage(error, t))
    },
    onSuccess: (items) => {
      setBoundApps(items)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteAdminAppTag,
    onSuccess: async () => {
      setDeleteOpen(false)
      setDeleteTarget(null)
      setDeleteError(undefined)
      setBoundApps([])
      showFeedback({
        message: t('feedback.deleteSuccess'),
        scope: 'global',
        tone: 'success',
      })
      await invalidateTagLists()
    },
    onError: (error) => {
      setDeleteError(resolveMutationErrorMessage(error, t))
    },
  })

  const openCreateDialog = () => {
    setFormMode('create')
    setFormTarget(null)
    setFormError(undefined)
    setFormOpen(true)
  }

  const openRenameDialog = useCallback((tag: AdminAppTag) => {
    setFormMode('rename')
    setFormTarget(tag)
    setFormError(undefined)
    setFormOpen(true)
  }, [])

  const openDeleteDialog = useCallback(
    (tag: AdminAppTag) => {
      setDeleteTarget(tag)
      setBoundApps([])
      setDeleteError(undefined)
      setDeleteOpen(true)
      loadBoundAppsMutation.mutate(tag.id)
    },
    [loadBoundAppsMutation],
  )

  const toggleSort = useCallback(
    (field: AdminAppTagSortField) => {
      const nextOrder = sortField === field && sortOrder === 'desc' ? 'asc' : 'desc'

      setSortField(field)
      setSortOrder(nextOrder)
      setPage(1)
    },
    [setPage, sortField, sortOrder],
  )
  const columns = useMemo<DataTableColumn<AdminAppTag>[]>(() => {
    const sortDirection = (field: AdminAppTagSortField) => (sortField === field ? sortOrder : null)
    const sortActionLabel = (field: AdminAppTagSortField, ascKey: string, descKey: string) =>
      sortField === field && sortOrder === 'desc' ? t(ascKey) : t(descKey)

    return [
      {
        header: t('table.name'),
        hidden: !visibleColumnIdSet.has('name'),
        id: 'name',
        render: (tag) => tag.name,
        visibilityLabel: t('table.name'),
        width: 220,
      },
      {
        header: t('table.appCount'),
        hidden: !visibleColumnIdSet.has('app-count'),
        id: 'app-count',
        render: (tag) => <AppCountCell count={tag.app_count} />,
        visibilityLabel: t('table.appCount'),
        width: 120,
      },
      {
        header: t('table.createdBy'),
        hidden: !visibleColumnIdSet.has('created-by'),
        id: 'created-by',
        render: (tag) => <OperatorCell id={tag.created_by} name={tag.created_by_name} />,
        visibilityLabel: t('table.createdBy'),
        width: 180,
      },
      {
        header: t('table.createdAt'),
        hidden: !visibleColumnIdSet.has('created-at'),
        id: 'created-at',
        render: (tag) => formatDateTime(tag.created_at),
        sort: {
          direction: sortDirection('created_at'),
          label: sortActionLabel('created_at', 'sort.createdAtAsc', 'sort.createdAtDesc'),
          onToggle: () => toggleSort('created_at'),
        },
        visibilityLabel: t('table.createdAt'),
        width: 190,
      },
      {
        header: t('table.updatedBy'),
        hidden: !visibleColumnIdSet.has('updated-by'),
        id: 'updated-by',
        render: (tag) => <OperatorCell id={tag.updated_by} name={tag.updated_by_name} />,
        visibilityLabel: t('table.updatedBy'),
        width: 180,
      },
      {
        header: t('table.updatedAt'),
        hidden: !visibleColumnIdSet.has('updated-at'),
        id: 'updated-at',
        render: (tag) => formatDateTime(tag.updated_at),
        sort: {
          direction: sortDirection('updated_at'),
          label: sortActionLabel('updated_at', 'sort.updatedAtAsc', 'sort.updatedAtDesc'),
          onToggle: () => toggleSort('updated_at'),
        },
        visibilityLabel: t('table.updatedAt'),
        width: 190,
      },
      {
        align: 'center',
        header: t('table.actions'),
        hidden: !visibleColumnIdSet.has('actions'),
        id: 'actions',
        pin: 'right',
        render: (tag) => (
          <ActionMenu
            ariaLabel={t('actions.open', { name: tag.name })}
            items={[
              {
                iconSrc: actionEditIconUrl,
                key: 'rename',
                label: t('actions.rename'),
                onSelect: () => openRenameDialog(tag),
              },
              {
                iconSrc: actionDeleteIconUrl,
                key: 'delete',
                label: t('actions.delete'),
                onSelect: () => openDeleteDialog(tag),
                variant: 'danger',
              },
            ]}
          />
        ),
        visibilityLabel: t('table.actions'),
        width: 90,
      },
    ]
  }, [openDeleteDialog, openRenameDialog, sortField, sortOrder, t, toggleSort, visibleColumnIdSet])

  const columnSelectorOptions = useMemo(
    () =>
      columns.map((column) => ({
        id: column.id,
        label: column.visibilityLabel ?? column.id,
      })),
    [columns],
  )

  const listData = tagsQuery.data
  const tags = listData?.items ?? []
  const total = listData?.total ?? 0
  const showError = tagsQuery.isError && !tagsQuery.data
  const errorDescription =
    tagsQuery.error instanceof ApiError
      ? t('errors.loadWithDetail', { detail: tagsQuery.error.message })
      : t('errors.loadFailed')

  const closeFormDialog = () => {
    if (createMutation.isPending || updateMutation.isPending) {
      return
    }

    setFormOpen(false)
    setFormTarget(null)
    setFormError(undefined)
  }

  const closeDeleteDialog = () => {
    if (deleteMutation.isPending) {
      return
    }

    setDeleteOpen(false)
    setDeleteTarget(null)
    setBoundApps([])
    setDeleteError(undefined)
  }

  return (
    <AdminShellPage activePath="/admin/app-tags">
      <section
        aria-label={t('workspaceLabel')}
        className="flex h-full min-w-0 flex-col overflow-hidden"
        data-testid="admin-app-tags-workspace"
      >
        <PageHeader
          actions={
            <>
              <DataTableColumnSelector
                aria-label={t('columnSelector.label')}
                onValueChange={setVisibleColumnIds}
                options={columnSelectorOptions}
                value={visibleColumnIds}
              >
                {t('columnSelector.trigger')}
              </DataTableColumnSelector>
              <Button onClick={openCreateDialog} size="sm">
                <PlusIcon />
                {t('actions.add')}
              </Button>
            </>
          }
          description={t('description')}
          title={t('title')}
        />
        <FilterBar>
          <SearchInput
            aria-label={t('filters.searchLabel')}
            onChange={(event) => {
              setSearchInput(event.target.value)
            }}
            placeholder={t('filters.searchPlaceholder')}
            value={searchInput}
          />
        </FilterBar>
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4 lg:px-[30px]">
          {showError ? (
            <ErrorState
              action={
                <Button
                  className="mt-3"
                  onClick={() => void tagsQuery.refetch()}
                  size="sm"
                  variant="secondary"
                >
                  {t('retry')}
                </Button>
              }
              description={errorDescription}
              title={t('errors.loadTitle')}
            />
          ) : (
            <>
              <DataTable
                columns={columns}
                empty={t('empty')}
                getRowKey={(tag) => tag.id}
                isLoading={tagsQuery.isLoading}
                rows={tags}
              />
              <Pagination
                disabled={tagsQuery.isFetching}
                labels={{
                  next: t('pagination.next'),
                  pageSize: (size) => t('pagination.pageSize', { count: size }),
                  previous: t('pagination.previous'),
                }}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                page={page}
                pageSize={pageSize}
                pageSizeOptions={pageSizeOptions}
                total={total}
              />
            </>
          )}
        </div>
        <TagFormDialog
          defaultName={formTarget?.name}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
          mode={formMode}
          onClose={closeFormDialog}
          onSubmit={async (values) => {
            setFormError(undefined)

            if (formMode === 'create') {
              await createMutation.mutateAsync(values)
              return
            }

            if (!formTarget) {
              setFormError(t('errors.notFound'))
              return
            }

            await updateMutation.mutateAsync({ tagId: formTarget.id, values })
          }}
          open={formOpen}
          rootError={formError}
        />
        <DeleteTagDialog
          apps={boundApps}
          isLoadingApps={loadBoundAppsMutation.isPending}
          isSubmitting={deleteMutation.isPending}
          onClose={closeDeleteDialog}
          onConfirm={() => {
            if (deleteTarget) {
              setDeleteError(undefined)
              deleteMutation.mutate(deleteTarget.id)
            }
          }}
          open={deleteOpen}
          rootError={deleteError}
          tag={deleteTarget ?? undefined}
        />
      </section>
    </AdminShellPage>
  )
}
